import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MOCK_DATASETS } from '../../data/mockData';
import SafeIcon from '../../common/SafeIcon';
import { FiFilter, FiMoreVertical, FiStar, FiDatabase, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

export default function Catalog() {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = MOCK_DATASETS.filter(ds => 
    ds.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    ds.dock.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--pearl)] mb-1">Dataset Docks</h2>
          <p className="text-[var(--pearl-muted)] text-sm">Browse and discover registered data assets.</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary flex items-center gap-2">
            <SafeIcon icon={FiFilter} /> Filters
          </button>
          <button className="btn-primary flex items-center gap-2">
            Register New Data
          </button>
        </div>
      </div>

      <div className="glass-panel flex-1 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-[var(--border-ocean)] flex gap-4">
          <input 
            type="text" 
            placeholder="Filter docks..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-[var(--bg-ocean)] border border-[var(--border-ocean)] rounded-md py-1.5 px-3 text-sm text-[var(--pearl)] focus:outline-none focus:border-[var(--schema-teal)]"
          />
        </div>
        
        <div className="flex-1 overflow-auto">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-[var(--surface-ocean)] z-10 border-b border-[var(--border-ocean)] shadow-md">
              <tr>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider">Asset Name</th>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider">Harbor Dock</th>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider">Owner</th>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider">Health</th>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider">Tags</th>
                <th className="p-4 text-xs font-semibold text-[var(--pearl-muted)] uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-ocean)]">
              {filtered.map((ds, i) => (
                <motion.tr 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  key={ds.id} 
                  className="hover:bg-[var(--surface-ocean-light)]/50 transition-colors group cursor-pointer"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-[var(--bg-ocean)] border border-[var(--border-ocean)] text-[var(--schema-teal)] group-hover:border-[var(--schema-teal)] transition-colors">
                        <SafeIcon icon={FiDatabase} />
                      </div>
                      <div>
                        <p className="font-medium text-[var(--pearl)] text-sm group-hover:text-[var(--schema-teal)] transition-colors">{ds.name}</p>
                        <p className="text-xs text-[var(--pearl-muted)]">{ds.type} • {ds.rows} rows</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-[var(--pearl-muted)]">{ds.dock}</td>
                  <td className="p-4 text-sm text-[var(--pearl-muted)]">{ds.owner}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {ds.health > 90 ? (
                        <SafeIcon icon={FiCheckCircle} className="text-[var(--success-mint)]" />
                      ) : (
                        <SafeIcon icon={FiAlertCircle} className="text-yellow-400" />
                      )}
                      <span className="text-sm font-medium">{ds.health}%</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {ds.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-[10px] rounded-full bg-[var(--bg-ocean)] border border-[var(--border-ocean)] text-[var(--pearl-muted)] group-hover:border-[var(--schema-teal-dim)]">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <button className="p-2 text-[var(--pearl-muted)] hover:text-[var(--schema-teal)] transition-colors">
                      <SafeIcon icon={FiMoreVertical} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-12 text-center text-[var(--pearl-muted)]">
              <SafeIcon icon={FiDatabase} className="text-4xl mx-auto mb-3 opacity-20" />
              <p>No datasets found in this harbor sector.</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}